<template>
    <div class="channels-page" v-if="channels != null && channels.length > 0">
        <div class="channels-grid">
            <router-link
                v-for="channel in channels"
                :key="channel.id"
                :to="`/@${channel.username}`"
                class="channel-card"
            >
                <div class="channel-avatar">
                    <img :src="getChannelAvatar(channel)" :alt="displayName(channel)" @error="handleAvatarError" />
                </div>
                <div class="channel-info">
                    <h3 class="channel-name">
                        {{ displayName(channel) }}
                        <vue-feather
                            v-if="channel.status === 'verified'"
                            type="check-circle"
                            size="14"
                            class="verified-badge"
                        />
                    </h3>
                    <p class="channel-username">@{{ channel.username }}</p>
                    <div class="channel-stats">
                        <div class="stat">
                            <vue-feather type="users" size="14" />
                            <span>{{ formatCount(channel.subscribersCount) }} subscribers</span>
                        </div>
                        <div class="stat">
                            <vue-feather type="video" size="14" />
                            <span>{{ channel.videos?.items?.length || 0 }} videos</span>
                        </div>
                        <div class="stat">
                            <vue-feather type="eye" size="14" />
                            <span>{{ formatCount(channel.viewsCount) }} views</span>
                        </div>
                    </div>
                    <p class="channel-bio" v-if="channel.bio">{{ truncateBio(channel.bio) }}</p>
                </div>
            </router-link>
        </div>
    </div>
    <div v-else class="empty-state">
        <vue-feather type="users" size="48" />
        <p>No channels found</p>
    </div>
</template>

<script>
export default {
    name: 'ChannelGrid',
    props: {
        channels: {
            type: Array,
            default() {
                return []
            }
        },
    },
    methods: {
        getChannelAvatar(channel) {
            if (!channel?.id) return '/avatar-placeholder.svg';
            if (channel?.legacyAvatar) {
                if (channel.legacyAvatar.startsWith('public/')) {
                    return `https://cdn1.eelam.tv/${channel.legacyAvatar}`;
                }
                return `https://cdn.eelam.tv/${channel.legacyAvatar}`;
            }
            return `https://cdn1.eelam.tv/public/images/user/${channel.id}/avatar.png`;
        },
        handleAvatarError(event) {
            if (event.target.src.endsWith('/avatar-placeholder.svg')) return;
            event.target.src = '/avatar-placeholder.svg';
        },
        displayName(channel) {
            if (channel.displayName) return channel.displayName;
            if (channel.firstName || channel.lastName) {
                return `${channel.firstName || ''} ${channel.lastName || ''}`.trim();
            }
            return channel.username || 'Unknown';
        },
        formatCount(count) {
            if (!count) return '0';
            if (count >= 1000000) {
                return (count / 1000000).toFixed(1) + 'M';
            }
            if (count >= 1000) {
                return (count / 1000).toFixed(1) + 'K';
            }
            return count.toString();
        },
        truncateBio(bio) {
            if (!bio) return '';
            return bio.length > 80 ? bio.substring(0, 80) + '...' : bio;
        }
    }
}
</script>

<style scoped>
.channels-page {
    padding: 20px;
    max-width: 1400px;
    margin: 0 auto;
}

.channels-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 24px;
}

.channel-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px;
    background: var(--bg-secondary, #1a1a1a);
    border-radius: 16px;
    text-decoration: none;
    color: inherit;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    border: 1px solid var(--border-color, #333);
}

.channel-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
    border-color: var(--primary-color, #ff4444);
}

.channel-avatar {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    overflow: hidden;
    margin-bottom: 16px;
    border: 3px solid var(--border-color, #333);
    transition: border-color 0.2s ease;
}

.channel-card:hover .channel-avatar {
    border-color: var(--primary-color, #ff4444);
}

.channel-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.channel-info {
    text-align: center;
    width: 100%;
}

.channel-name {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 4px 0;
    color: var(--text-primary, #fff);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.verified-badge {
    color: var(--primary-color, #ff4444);
}

.channel-username {
    font-size: 14px;
    color: var(--text-secondary, #888);
    margin: 0 0 16px 0;
}

.channel-stats {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 16px;
    margin-bottom: 12px;
}

.stat {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--text-secondary, #888);
}

.stat svg {
    color: var(--text-tertiary, #666);
}

.channel-bio {
    font-size: 13px;
    color: var(--text-secondary, #888);
    margin: 8px 0 0 0;
    line-height: 1.4;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: var(--text-tertiary, #666);
}

.empty-state svg {
    margin-bottom: 16px;
    opacity: 0.5;
}

.empty-state p {
    font-size: 18px;
    margin: 0;
}

@media (max-width: 768px) {
    .channels-grid {
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        gap: 16px;
    }

    .channel-card {
        padding: 20px;
    }

    .channel-avatar {
        width: 80px;
        height: 80px;
    }

    .channel-stats {
        flex-direction: column;
        gap: 8px;
    }
}
</style>
